import React, { useState, useEffect } from "react";
import { http } from "../../lib/api";
import { X, CheckCircle, Clock, AlertCircle, Monitor } from "lucide-react";

export default function CerrarOrdenMantenimiento({ orden, onClose, onSuccess }) {
  const [equipos, setEquipos] = useState([]);
  const [equiposMantenidos, setEquiposMantenidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    const scrollContainer = document.querySelector('[data-app-scroll-container]');
    const previousOverflow = scrollContainer?.style.overflow;
    const previousOverscroll = scrollContainer?.style.overscrollBehavior;
    const previousBodyOverflow = document.body.style.overflow;

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      scrollContainer.style.overflow = 'hidden';
      scrollContainer.style.overscrollBehavior = 'contain';
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow || '';
      if (scrollContainer) {
        scrollContainer.style.overflow = previousOverflow || '';
        scrollContainer.style.overscrollBehavior = previousOverscroll || '';
      }
    };
  }, []);

  useEffect(() => {
    if (orden?.agendamiento_id) {
      cargarEquiposAgendamiento();
    }
  }, [orden]);

  async function cargarEquiposAgendamiento() {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // Obtener detalles del agendamiento
      const response = await http.get(`/agendamientos-inteligentes/${orden.agendamiento_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.equipos) {
        // Cargar detalles de cada equipo
        const equiposPromises = response.data.equipos.map(async (equipoId) => {
          try {
            const equipoRes = await http.get(`/equipos/${equipoId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            return {
              id: equipoId,
              ...equipoRes.data,
              estado: response.data.equipos_estado?.[equipoId] || "programado"
            };
          } catch (err) {
            console.error(`Error cargando equipo ${equipoId}:`, err);
            return {
              id: equipoId,
              nombre: "Equipo no disponible",
              estado: "programado"
            };
          }
        });
        
        const equiposData = await Promise.all(equiposPromises);
        setEquipos(equiposData);
        
        // Pre-seleccionar equipos que ya están marcados como mantenidos
        const yaMantenidos = equiposData
          .filter(eq => eq.estado === "mantenido")
          .map(eq => eq.id);
        setEquiposMantenidos(yaMantenidos);
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error cargando equipos:", err);
      setError("No se pudieron cargar los equipos del agendamiento");
      setLoading(false);
    }
  }

  function toggleEquipo(equipoId) {
    setEquiposMantenidos(prev => {
      if (prev.includes(equipoId)) {
        return prev.filter(id => id !== equipoId);
      } else {
        return [...prev, equipoId];
      }
    });
  }

  async function handleCerrarOrden() {
    try {
      setSubmitting(true);
      setError(null);
      
      const token = localStorage.getItem("token");
      await http.post(
        `/ordenes/${orden._id}/cerrar-con-mantenimiento`,
        { equipos_mantenidos: equiposMantenidos },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error cerrando orden:", err);
      setError(err.response?.data?.detail || "Error al cerrar la orden");
      setSubmitting(false);
    }
  }

  if (!orden?.es_servicio_programado) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="h-6 w-6 text-amber-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Esta no es una orden programada
            </h2>
          </div>
          <p className="text-gray-600 mb-6">
            Solo se puede cerrar con seguimiento de mantenimiento las órdenes que provienen de servicios programados.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Cerrar Orden de Mantenimiento
              </h2>
              <p className="text-sm text-gray-600">
                Orden #{orden.numero_orden || orden._id?.slice(-6)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Selecciona los equipos que fueron mantenidos
            </h3>
            <p className="text-sm text-gray-600">
              Los equipos no marcados se reorganizarán automáticamente en futuras visitas.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <Clock className="h-8 w-8 text-emerald-600 animate-spin" />
                <p className="text-gray-600">Cargando equipos...</p>
              </div>
            </div>
          ) : equipos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
              <Monitor className="h-12 w-12 text-gray-400 mb-3" />
              <p className="text-gray-600">No hay equipos programados en esta orden</p>
            </div>
          ) : (
            <div className="space-y-2 mb-6">
              {equipos.map((equipo) => (
                <label
                  key={equipo.id}
                  className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    equiposMantenidos.includes(equipo.id)
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={equiposMantenidos.includes(equipo.id)}
                    onChange={() => toggleEquipo(equipo.id)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Monitor className="h-4 w-4 text-gray-600" />
                      <span className="font-medium text-gray-900">
                        {equipo.nombre || equipo.tipo || "Equipo sin nombre"}
                      </span>
                    </div>
                    {equipo.modelo && (
                      <p className="text-sm text-gray-600">
                        Modelo: {equipo.modelo}
                      </p>
                    )}
                    {equipo.numero_serie && (
                      <p className="text-sm text-gray-600">
                        S/N: {equipo.numero_serie}
                      </p>
                    )}
                    {equipo.estado === "mantenido" && (
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                        <CheckCircle className="h-3 w-3" />
                        Ya mantenido
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Summary */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Equipos mantenidos:</span>
              <span className="font-semibold text-emerald-900">
                {equiposMantenidos.length} de {equipos.length}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-700">Equipos pendientes:</span>
              <span className="font-semibold text-amber-900">
                {equipos.length - equiposMantenidos.length}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleCerrarOrden}
              disabled={submitting || loading || equipos.length === 0}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  Cerrando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Cerrar Orden
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
